import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { api, getToken, setToken, clearToken } from './utils/api';
import Dashboard from './pages/Dashboard';
import Branches from './pages/Branches';
import Declarations from './pages/Declarations';
import Approvals from './pages/Approvals';
import Royalties from './pages/Royalties';
import Profit from './pages/Profit';
import Users from './pages/Users';
import Pipeline from './pages/Pipeline';
import Activity from './pages/Activity';
import Compliance from './pages/Compliance';
import Xero from './pages/Xero';
import CFODashboard from './pages/CFODashboard';
import FranchiseePortal from './pages/FranchiseePortal';
import InvestorPortal from './pages/InvestorPortal';

// Permission key for each nav item (null = always visible)
const NAV_PERMISSION_MAP = {
  dashboard:    null,
  branches:     'view_branches',
  declarations: 'view_declarations',
  approvals:    'approve_declarations',
  royalties:    'view_royalties',
  profit:       'view_profit',
  users:        'view_users',
  pipeline:     'view_pipeline',
  compliance:   'view_compliance',
  activity:     'view_activity',
  xero:         'view_xero',
  cfo:          'view_cfo',
};

const ALL_ADMIN_NAV = [
  { key:'dashboard',    label:'Dashboard',    icon:'ti-layout-dashboard' },
  { key:'branches',     label:'Branches',     icon:'ti-building-store' },
  { key:'declarations', label:'Declarations', icon:'ti-file-text' },
  { key:'approvals',    label:'Approvals',    icon:'ti-checks', badge:'approvalCount' },
  { key:'royalties',    label:'Royalties',    icon:'ti-coin' },
  { key:'profit',       label:'Profit',       icon:'ti-trending-up' },
  { key:'users',        label:'Users',        icon:'ti-users' },
  { key:'pipeline',     label:'Pipeline',     icon:'ti-git-merge' },
  { key:'compliance',   label:'Compliance',   icon:'ti-file-check' },
  { key:'activity',     label:'Activity',     icon:'ti-activity' },
  { key:'xero',         label:'Xero',         icon:'ti-file-invoice', xeroFailed:'xeroFailed' },
  { key:'cfo',          label:'CFO Dashboard',icon:'ti-chart-bar' },
];

const FRANCHISEE_NAV = [
  { key:'portal_dashboard',    label:'Dashboard',    icon:'ti-layout-dashboard' },
  { key:'portal_declarations', label:'Declarations', icon:'ti-file-text' },
  { key:'portal_royalties',    label:'Royalties',    icon:'ti-coin' },
  { key:'portal_profit',       label:'Profit',       icon:'ti-trending-up' },
];

const INVESTOR_NAV = [
  { key:'portal_dashboard', label:'Dashboard', icon:'ti-layout-dashboard' },
  { key:'portal_profit',    label:'Profit',    icon:'ti-trending-up' },
  { key:'portal_royalties', label:'Royalties', icon:'ti-coin' },
];

function canAccess(user, navKey) {
  if (user.role === 'admin') return true;
  if (user.role === 'hq_support') {
    const permKey = NAV_PERMISSION_MAP[navKey];
    if (permKey === null) return false; // dashboard always hidden for hq_support
    const perms = typeof user.permissions === 'string'
      ? (() => { try { return JSON.parse(user.permissions); } catch(e) { return {}; } })()
      : (user.permissions || {});
    return perms[permKey] === true;
  }
  return true;
}

function getNavItems(user) {
  if (user.role === 'admin' || user.role === 'director') return ALL_ADMIN_NAV;
  if (user.role === 'hq_support') {
    // Exclude dashboard entirely for hq_support, only show permitted pages
    const filtered = ALL_ADMIN_NAV.filter(item => item.key !== 'dashboard' && canAccess(user, item.key));
    return filtered;
  }
  return ALL_ADMIN_NAV;
}

function getDefaultView(user) {
  if (user.role !== 'hq_support') return 'dashboard';
  // Find first permitted page for hq_support
  const permitted = ALL_ADMIN_NAV.find(item => item.key !== 'dashboard' && canAccess(user, item.key));
  return permitted ? permitted.key : null;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setViewState] = useState(() => {
    const hash = (window.location.hash.replace('#', '') || 'dashboard').split(':')[0];
    return hash;
  });
  const [directorView, setDirectorView] = useState(false);
  const [approvalCount, setApprovalCount] = useState(0);
  const [xeroFailed, setXeroFailed] = useState(0);


  const setView = (v) => {
    setViewState(v);
    window.location.hash = v;
  };

  useEffect(() => {
    const onHashChange = () => {
      const hash = (window.location.hash.replace('#', '') || 'dashboard').split(':')[0];
      setViewState(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    api.me().then(u => {
      if (u?.id) { setUser(u); loadCounts(); mountAccountModal(u); }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadCounts = () => {
    api.getDeclarations().then(d => {
      setApprovalCount(Array.isArray(d) ? d.filter(decl => decl.status==='pending'||decl.editRequested).length : 0);
    }).catch(()=>{});
    api.getXeroLog().then(l => {
      setXeroFailed(Array.isArray(l) ? l.filter(x => x.status==='failed').length : 0);
    }).catch(()=>{});
  };

  const handleLogin = async (email, password) => {
    const res = await api.login(email, password);
    if (res?.token) {
      setToken(res.token);
      setUser(res.user);
      loadCounts();
      mountAccountModal(res.user);
      // Redirect HQ support to their first permitted page
      if (res.user?.role === 'hq_support') {
        const defaultView = getDefaultView(res.user);
        setView(defaultView);
      }
    } else {
      throw new Error(res?.error || 'Login failed');
    }
  };

  const handleLogout = () => {
    clearToken();
    setUser(null);
    setViewState('dashboard');
    window.location.hash = '';
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f8faff' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTopColor:'#1c1960', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
    </div>
  );

  if (!user) return <LoginPage onLogin={handleLogin} />;

  const isAdmin = user.role === 'admin';
  const isHQSupport = user.role === 'hq_support';
  const isDirector = user.role === 'director';
  const isFranchisee = user.role === 'franchisee';
  const isInvestor = user.role === 'investor';
  const showAdminUI = isAdmin || isHQSupport || (isDirector && directorView);
  const navItems = showAdminUI ? getNavItems(user) : isFranchisee ? FRANCHISEE_NAV : isInvestor ? INVESTOR_NAV : FRANCHISEE_NAV;

  // Access denied component for hq_support
  const AccessDenied = () => (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', flexDirection:'column', gap:12 }}>
      <i className="ti ti-lock" style={{ fontSize:48, color:'var(--text-tertiary)' }} />
      <div style={{ fontSize:18, fontWeight:600, color:'var(--navy)' }}>Access Restricted</div>
      <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>You don't have permission to view this page.</div>
    </div>
  );

  const renderPage = () => {
    if (isInvestor) return <InvestorPortal user={user} view={view} setView={setView} />;
    if (!showAdminUI) return <FranchiseePortal user={user} view={view} setView={setView} directorView={directorView} onEnterDirectorView={() => { setDirectorView(true); setView('dashboard'); }} />;

    // Redirect hq_support away from dashboard or unpermitted pages
    if (isHQSupport) {
      if (view === 'dashboard' || !canAccess(user, view)) {
        const defaultView = getDefaultView(user);
        if (!defaultView) {
          // No permissions set at all
          return (
            <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', flexDirection:'column', gap:12 }}>
              <i className="ti ti-lock" style={{ fontSize:48, color:'var(--text-tertiary)' }} />
              <div style={{ fontSize:18, fontWeight:600, color:'var(--navy)' }}>No pages available</div>
              <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Contact your administrator to be granted access to pages.</div>
            </div>
          );
        }
        return <PipelineRedirect setView={setView} target={defaultView} />;
      }
    }

    switch(view) {
      case 'dashboard':    return <Dashboard user={user} setView={setView} directorView={directorView} onExitDirectorView={isDirector ? () => { setDirectorView(false); setView('dashboard'); } : null} />;
      case 'branches':     return <Branches user={user} directorView={directorView} />;
      case 'declarations': return <Declarations user={user} directorView={directorView} />;
      case 'approvals':    return <Approvals user={user} directorView={directorView} />;
      case 'royalties':    return <Royalties user={user} directorView={directorView} />;
      case 'profit':       return <Profit user={user} directorView={directorView} />;
      case 'users':        return <Users user={user} directorView={directorView} />;
      case 'pipeline':     return null; // Always mounted separately below
      case 'compliance':   return <Compliance user={user} />;
      case 'activity':     return <Activity user={user} />;
      case 'xero':         return <Xero user={user} directorView={directorView} />;
      case 'cfo':          return <CFODashboard user={user} />;
      default:             return <Dashboard user={user} setView={setView} directorView={directorView} />;
    }
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)' }}>
      <Sidebar
        user={user}
        navItems={navItems}
        view={view}
        setView={setView}
        onLogout={handleLogout}
        onAccount={() => window.__openAccountModal && window.__openAccountModal()}
        approvalCount={approvalCount}
        xeroFailed={xeroFailed}
        isDirector={isDirector}
        directorView={directorView}
        onExitDirectorView={() => { setDirectorView(false); setView('dashboard'); }}
      />
      <div style={{ flex:1, minWidth:0 }}>
        {showAdminUI && (
          <div style={{ display: view === 'pipeline' ? 'block' : 'none' }}>
            <Pipeline user={user} />
          </div>
        )}
        {view !== 'pipeline' && renderPage()}
      </div>
    </div>
  );
}

function PipelineRedirect({ setView, target }) {
  useEffect(() => { setView(target); }, []);
  return null;
}

function Sidebar({ user, navItems, view, setView, onLogout, onAccount, approvalCount, xeroFailed, isDirector, directorView, onExitDirectorView }) {
  return (
    <div style={{
      width:'var(--sidebar-width)', minHeight:'100vh', background:'var(--navy)',
      display:'flex', flexDirection:'column', position:'fixed', left:0, top:0, bottom:0, zIndex:50,
      boxShadow:'2px 0 20px rgba(0,0,0,0.08)',
    }}>
      <div style={{ padding:'16px 16px 14px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <img src="https://www.goinvestors.co.uk/finance-wordmark-white.svg" alt="GoInvestors Finance" style={{ height:36, display:'block', filter:'brightness(10)' }} />
      </div>
      {isDirector && directorView && (
        <div style={{ padding:'8px 12px', background:'rgba(255,255,255,0.1)', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:11, color:'rgba(255,255,255,0.7)' }}>Director View</span>
          <button onClick={onExitDirectorView} style={{ background:'rgba(255,255,255,0.15)', border:'none', cursor:'pointer', fontSize:10, color:'#fff', padding:'3px 8px', borderRadius:4 }}>← Exit</button>
        </div>
      )}
      <nav style={{ flex:1, padding:'10px 8px', overflowY:'auto' }}>
        {navItems.map(item => {
          const isActive = view === item.key;
          const badge = item.badge === 'approvalCount' ? approvalCount : item.xeroFailed === 'xeroFailed' ? xeroFailed : 0;
          return (
            <button key={item.key} onClick={() => setView(item.key)} style={{
              width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 10px',
              borderRadius:'var(--r-md)', marginBottom:2, border:'none', cursor:'pointer', textAlign:'left',
              background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
              color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
              transition:'all 0.15s',
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background='rgba(255,255,255,0.07)'; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background='transparent'; }}>
              <i className={`ti ${item.icon}`} style={{ fontSize:16, flexShrink:0, opacity: isActive?1:0.8 }} />
              <span style={{ fontSize:13, fontWeight: isActive?600:400, flex:1 }}>{item.label}</span>
              {badge > 0 && (
                <span style={{ background:'rgba(239,68,68,0.9)', color:'#fff', borderRadius:10, fontSize:10, fontWeight:700, padding:'1px 6px', minWidth:18, textAlign:'center' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div style={{ padding:'12px 8px', borderTop:'1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:'var(--r-md)', marginBottom:4 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>
            {(user?.name||'?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', textTransform:'capitalize' }}>{user?.role?.replace('_',' ')}</div>
          </div>
        </div>
        <button onClick={() => onAccount()} style={{ width:'100%', padding:'8px 10px', border:'none', cursor:'pointer', background:'transparent', color:'rgba(255,255,255,0.45)', fontSize:12, display:'flex', alignItems:'center', gap:8, borderRadius:'var(--r-md)' }}
          onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,0.75)'}
          onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.45)'}>
          <i className="ti ti-user-circle" style={{ fontSize:14 }} /> My Account
        </button>
        <button onClick={onLogout} style={{ width:'100%', padding:'8px 10px', border:'none', cursor:'pointer', background:'transparent', color:'rgba(255,255,255,0.45)', fontSize:12, display:'flex', alignItems:'center', gap:8, borderRadius:'var(--r-md)' }}
          onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,0.75)'}
          onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.45)'}>
          <i className="ti ti-logout" style={{ fontSize:14 }} /> Sign Out
        </button>
      </div>
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const handleSubmit = async () => {
    if (!email || !password) return;
    setLoading(true); setError('');
    try { await onLogin(email, password); }
    catch(err) { setError(err.message || 'Login failed'); }
    setLoading(false);
  };
  const handleForgot = async () => {
    if (!forgotEmail) return;
    setLoading(true);
    try { await api.forgotPassword(forgotEmail); setForgotSent(true); }
    catch(err) { setError('Failed to send reset email'); }
    setLoading(false);
  };
  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg, #1c1960 0%, #2d2a8e 50%, #1c1960 100%)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:'40px 36px', width:'100%', maxWidth:400, boxShadow:'0 24px 80px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <img src="/favicon-192.png" alt="GoInvestors" style={{ height:72, display:'block', margin:'0 auto' }} />
        </div>
        {!forgotMode ? (
          <div>
            <div style={{ marginBottom:14 }}>
              <label>EMAIL ADDRESS</label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@gotutors.com"
                onKeyDown={e => e.key==='Enter' && handleSubmit()} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label>PASSWORD</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"
                onKeyDown={e => e.key==='Enter' && handleSubmit()} />
            </div>
            {error && <div style={{ padding:'10px 14px', background:'rgba(163,45,45,0.08)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--red)', marginBottom:12 }}>{error}</div>}
            <button type="button" className="btn btn-primary" style={{ width:'100%', padding:'12px', fontSize:14, borderRadius:'var(--r-md)', display:'flex', alignItems:'center', justifyContent:'center' }} disabled={loading} onClick={handleSubmit}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button type="button" onClick={() => setForgotMode(true)} style={{ display:'block', marginTop:12, width:'100%', background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--text-tertiary)', textAlign:'center' }}>
              Forgot your password?
            </button>
          </div>
        ) : (
          <div>
            {forgotSent ? (
              <div style={{ textAlign:'center', padding:'20px 0' }}>
                <i className="ti ti-mail-check" style={{ fontSize:40, color:'var(--green)', display:'block', marginBottom:12 }} />
                <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>Reset link sent!</div>
                <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>Check your email for a password reset link.</div>
                <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }} className="btn btn-secondary" style={{ marginTop:16, width:'100%' }}>Back to login</button>
              </div>
            ) : (
              <>
                <div style={{ fontSize:14, fontWeight:500, marginBottom:16, color:'var(--navy)' }}>Reset your password</div>
                <div style={{ marginBottom:14 }}>
                  <label>EMAIL ADDRESS</label>
                  <input type="email" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} placeholder="you@gotutors.com"
                    onKeyDown={e => e.key==='Enter' && handleForgot()} />
                </div>
                {error && <div style={{ padding:'10px 14px', background:'rgba(163,45,45,0.08)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--red)', marginBottom:12 }}>{error}</div>}
                <button type="button" className="btn btn-primary" style={{ width:'100%', marginBottom:10 }} disabled={loading} onClick={handleForgot}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => setForgotMode(false)} className="btn btn-secondary" style={{ width:'100%' }}>Cancel</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// Mount account modal completely outside App's React tree
function mountAccountModal(user) {
  let container = document.getElementById('account-modal-root');
  if (!container) {
    container = document.createElement('div');
    container.id = 'account-modal-root';
    document.body.appendChild(container);
  }
  function ModalRoot() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
      window.__openAccountModal = () => setOpen(true);
    }, []);
    if (!open) return null;
    return <AccountModal user={user} onClose={() => setOpen(false)} />;
  }
  const root = ReactDOM.createRoot ? ReactDOM.createRoot(container) : null;
  if (root) {
    root.render(<ModalRoot />);
  } else {
    ReactDOM.render(<ModalRoot />, container);
  }
}


function AccountModal({ user, onClose }) {
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confPw, setConfPw] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('details');
  async function handleChangePassword() {
    if (!curPw || !newPw || !confPw) { setErr('All fields required'); return; }
    if (newPw.length < 8) { setErr('New password must be at least 8 characters'); return; }
    if (newPw !== confPw) { setErr('Passwords do not match'); return; }
    setLoading(true); setErr(null); setMsg(null);
    try {
      const res = await fetch('https://api.goinvestors.co.uk/api/users/' + user.id + '/change-password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('gi_token')||'') },
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw })
      });
      const data = await res.json();
      if (data.error) { setErr(data.error); }
      else { setMsg('Password changed successfully!'); setCurPw(''); setNewPw(''); setConfPw(''); }
    } catch(e) { setErr('Connection error. Please try again.'); }
    setLoading(false);
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:'var(--r-lg)', width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.2)', overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:15, fontWeight:700, color:'var(--navy)' }}>My Account</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-tertiary)' }}>✕</button>
        </div>
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)' }}>
          {[['details','Details'],['password','Change Password']].map(([key,label]) => (
            <button key={key} onClick={()=>setTab(key)} style={{ flex:1, padding:'10px', border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background:'transparent', borderBottom: tab===key?'2px solid var(--navy)':'2px solid transparent', color:tab===key?'var(--navy)':'var(--text-tertiary)' }}>{label}</button>
          ))}
        </div>
        <div style={{ padding:20 }}>
          {tab === 'details' && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:8 }}>
                <div style={{ width:52, height:52, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:'#fff' }}>
                  {(user?.name||'?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:16, color:'var(--navy)' }}>{user?.name}</div>
                  <div style={{ fontSize:12, color:'var(--text-tertiary)', textTransform:'capitalize', marginTop:2 }}>{user?.role?.replace('_',' ')}</div>
                </div>
              </div>
              <div><label>EMAIL</label><div style={{ fontSize:13, padding:'8px 12px', background:'var(--bg)', borderRadius:'var(--r-md)' }}>{user?.email}</div></div>
              <div><label>ROLE</label><div style={{ fontSize:13, padding:'8px 12px', background:'var(--bg)', borderRadius:'var(--r-md)', textTransform:'capitalize' }}>{user?.role?.replace('_',' ')}</div></div>
            </div>
          )}
          {tab === 'password' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div><label>CURRENT PASSWORD</label><input type="password" placeholder="Current password" value={curPw} onChange={e=>setCurPw(e.target.value)} /></div>
              <div><label>NEW PASSWORD</label><input type="password" placeholder="Min 8 characters" value={newPw} onChange={e=>setNewPw(e.target.value)} /></div>
              <div><label>CONFIRM NEW PASSWORD</label><input type="password" placeholder="Repeat new password" value={confPw} onChange={e=>setConfPw(e.target.value)} /></div>
              {err && <div style={{ padding:'10px 14px', background:'var(--red-bg)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--red)' }}>{err}</div>}
              {msg && <div style={{ padding:'10px 14px', background:'var(--green-bg)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--green)' }}>{msg}</div>}
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={loading}>
                {loading ? 'Saving...' : 'Change Password'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
